import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SavedSession } from '@/types/session';
import { CachedAvatar } from '@/shared/components/ui/cached-avatar';
import { Input } from '@/shared/components/ui/Input';
import { Icon } from '@/shared/icons';
import { decodeJwtClaims } from '@/lib/jwt';

interface SessionItemProps {
  session: SavedSession;
  onLogin: () => void;
  onDelete: () => void;
}

/**
 * Linha de sessão salva. Container é <div role="button"> (clicável) e o botão
 * de excluir é um <button> real com stopPropagation — evita <button> aninhado.
 */
const SessionItem: React.FC<SessionItemProps> = ({ session, onLogin, onDelete }) => {
  const expiresAt = decodeJwtClaims(session.token)?.exp;
  const isExpired = typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now();

  return (
    <div
      role="button"
      tabIndex={0}
      className="mb-2 flex w-full cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors"
      style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-input-border)' }}
      onClick={onLogin}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLogin(); } }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        e.currentTarget.style.borderColor = 'var(--theme-primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--theme-card-background)';
        e.currentTarget.style.borderColor = 'var(--theme-input-border)';
      }}
      data-testid={`saved-session-${session.id}`}
    >
      <div className="flex-shrink-0">
        <CachedAvatar src={session.avatar} alt={session.name} size="md" isSquircle={true} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
          {session.name}
        </span>
        <span className="truncate text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {session.email}
        </span>
      </div>
      {isExpired && (
        <span
          className="flex-shrink-0 rounded-[5px] px-[7px] py-[2px] text-[11px] font-semibold"
          style={{ color: '#d97706', backgroundColor: 'rgba(245,158,11,0.14)' }}
          data-testid={`saved-session-expired-${session.id}`}
        >
          Sessão expirada
        </span>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors"
        style={{ color: 'var(--theme-card-muted)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.10)'; e.currentTarget.style.color = 'var(--theme-error-color)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--theme-card-muted)'; }}
        title="Remover conta"
        aria-label={`Remover ${session.name}`}
        data-testid={`saved-session-delete-${session.id}`}
      >
        <Icon name="trash" className="h-4 w-4" />
      </button>
    </div>
  );
};

SessionItem.displayName = 'SessionItem';

interface SavedSessionsListProps {
  sessions: SavedSession[];
  onSessionLogin: (session: SavedSession) => void;
  onSessionDelete: (sessionId: string) => void;
  primaryColor?: string;
}

/** Overrides de tema (dark forçado do auth) para o modal portalado ao body. */
const MODAL_DARK_VARS: React.CSSProperties = {
  '--theme-text': '#f1f5f9',
  '--theme-muted-text': '#9aa6b8',
  '--theme-card-background': '#161f30',
  '--theme-card-muted': '#6b7890',
  '--theme-input-background': '#111a29',
  '--theme-input-text': '#f1f5f9',
  '--theme-input-border': 'rgba(255,255,255,0.14)',
  '--theme-button-ghost-hover': 'rgba(255,255,255,0.05)',
  '--theme-error-color': '#f87171',
} as React.CSSProperties;

export const SavedSessionsList: React.FC<SavedSessionsListProps> = ({
  sessions,
  onSessionLogin,
  onSessionDelete,
  primaryColor = '#8b5cf6',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return sessions;
    const term = search.toLowerCase();
    return sessions.filter(
      (s) => s.name.toLowerCase().includes(term) || s.email.toLowerCase().includes(term)
    );
  }, [sessions, search]);

  if (sessions.length === 0) {
    return null;
  }

  // Mockup: 2 sessões inline + "Ver todas as contas (N)"
  const displayedSessions = sessions.slice(0, 2);
  const hasMoreSessions = sessions.length > 2;

  const closeModal = () => {
    setShowModal(false);
    setSearch('');
  };

  return (
    <>
      <div className="mb-5">
        <h3
          className="mb-2.5 text-[11px] font-bold uppercase tracking-wider"
          style={{ color: 'var(--theme-card-muted)' }}
        >
          Contas salvas
        </h3>

        {displayedSessions.map((session) => (
          <SessionItem
            key={session.id}
            session={session}
            onLogin={() => onSessionLogin(session)}
            onDelete={() => onSessionDelete(session.id)}
          />
        ))}

        {/* Ver todas — abre modal */}
        {hasMoreSessions && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="w-full rounded-lg py-2.5 text-center text-[13px] font-semibold transition-colors"
            style={{ color: 'var(--theme-primary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid="saved-sessions-view-all"
          >
            Ver todas as contas ({sessions.length})
          </button>
        )}

        {/* Divider "ou entre com outra conta" */}
        <div
          className="my-5 flex items-center gap-3 text-[12px]"
          style={{ color: 'var(--theme-card-muted)' }}
        >
          <span className="h-px flex-1" style={{ backgroundColor: 'var(--theme-border)' }} />
          ou entre com outra conta
          <span className="h-px flex-1" style={{ backgroundColor: 'var(--theme-border)' }} />
        </div>
      </div>

      {/* Modal: todas as contas salvas (segue o dark forçado do auth) */}
      {showModal && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          data-testid="saved-sessions-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={closeModal}
          />

          {/* Conteúdo */}
          <div
            className="relative flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border"
            style={{
              backgroundColor: '#161f30',
              borderColor: 'rgba(255,255,255,0.14)',
              boxShadow: '0 18px 60px rgba(0,0,0,0.5)',
              '--theme-input-focus': primaryColor,
              '--ring-color': `${primaryColor}29`,
              ...MODAL_DARK_VARS,
            } as React.CSSProperties}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between border-b px-5 py-4"
              style={{ borderColor: 'var(--theme-input-border)' }}
            >
              <h3 className="text-base font-bold" style={{ color: 'var(--theme-text)' }}>
                Suas contas
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                style={{ color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                aria-label="Fechar"
                data-testid="saved-sessions-modal-close"
              >
                <Icon name="x" className="h-5 w-5" />
              </button>
            </div>

            {/* Busca */}
            <div className="px-5 pb-2 pt-4">
              <Input
                type="text"
                icon="search"
                placeholder="Buscar por nome ou e-mail"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="saved-sessions-search"
              />
            </div>

            {/* Lista de sessões */}
            <div className="flex-1 space-y-0 overflow-y-auto px-5 pb-5 pt-2">
              {filteredSessions.length === 0 ? (
                <p className="py-8 text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhuma conta encontrada
                </p>
              ) : (
                filteredSessions.map((session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    onLogin={() => { onSessionLogin(session); closeModal(); }}
                    onDelete={() => onSessionDelete(session.id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

SavedSessionsList.displayName = 'SavedSessionsList';
